import { isEmpty } from 'lodash'

const BATCH_SIZE = 3

const extractBasename = (path) => {
  if (!path) return path
  const segments = String(path).split('/')
  return segments[segments.length - 1] || path
}

const prepareAttachment = async (attachment) => {
  const filename = attachment?.name || `anexo-${attachment?.id}.pdf`
  const response = await fetch(attachment.url)
  if (!response.ok) throw new Error(`Falha ao baixar: ${filename}`)
  const blob = await response.blob()
  const file = new File([blob], filename, {
    type: blob.type || 'application/pdf',
  })
  return { file, filename }
}

const uploadDisputeAttachments = async ({
  governanceProjectId,
  governanceProjectActivityId,
  selectedAttachments,
  localFiles,
  uploadEvidence,
  listEvidences,
  reconcileEvidences,
  onProgress,
}) => {
  const succeeded = []
  const failed = []

  const targetFilenames = [
    ...selectedAttachments.map(
      (attachment) => attachment?.name || `anexo-${attachment?.id}.pdf`,
    ),
    ...localFiles.map((file) => file.name),
  ]

  let remoteFilenames = new Set()
  if (listEvidences) {
    try {
      const response = await listEvidences({
        governanceProjectId,
        governanceProjectActivityId,
      })
      const remotePaths = response?.data?.evidences ?? []
      remoteFilenames = new Set(remotePaths.map(extractBasename))
    } catch (error) {
      console.error('Erro ao listar evidências remotas:', error)
    }
  }

  if (reconcileEvidences) {
    try {
      const reconcileResponse = await reconcileEvidences({
        governanceProjectId,
        governanceProjectActivityId,
        keepFilenames: targetFilenames,
      })
      const kept = reconcileResponse?.data?.kept ?? []
      remoteFilenames = new Set(kept)
    } catch (error) {
      console.error('Erro ao reconciliar evidências remotas:', error)
      throw error
    }
  }

  const attachmentsToUpload = selectedAttachments.filter((attachment) => {
    const filename = attachment?.name || `anexo-${attachment?.id}.pdf`
    return !remoteFilenames.has(filename)
  })
  const localFilesToUpload = localFiles.filter(
    (file) => !remoteFilenames.has(file.name),
  )

  const alreadyPresent = [
    ...selectedAttachments
      .map((attachment) => attachment?.name || `anexo-${attachment?.id}.pdf`)
      .filter((filename) => remoteFilenames.has(filename)),
    ...localFiles
      .map((file) => file.name)
      .filter((filename) => remoteFilenames.has(filename)),
  ]
  succeeded.push(...alreadyPresent)

  const total = attachmentsToUpload.length + localFilesToUpload.length
  const reportProgress = () => {
    if (onProgress) onProgress(succeeded.length + failed.length, total)
  }

  if (total > 0) reportProgress()

  const uploadFile = async (file, filename) => {
    try {
      await uploadEvidence({
        governanceProjectId,
        governanceProjectActivityId,
        file,
      })
      succeeded.push(filename)
    } catch (error) {
      console.error('Erro ao enviar arquivo:', error)
      failed.push(filename)
    }
    reportProgress()
  }

  const prepareOne = async (attachment) => {
    try {
      return await prepareAttachment(attachment)
    } catch (error) {
      console.error('Erro ao baixar anexo:', error)
      failed.push(attachment?.name || `anexo-${attachment?.id}.pdf`)
      reportProgress()
      return null
    }
  }

  if (!isEmpty(attachmentsToUpload)) {
    for (
      let index = 0;
      index < attachmentsToUpload.length;
      index += BATCH_SIZE
    ) {
      const batch = attachmentsToUpload.slice(index, index + BATCH_SIZE)
      const prepared = (await Promise.all(batch.map(prepareOne))).filter(
        Boolean,
      )
      await Promise.all(
        prepared.map(({ file, filename }) => uploadFile(file, filename)),
      )
    }
  }

  if (!isEmpty(localFilesToUpload)) {
    for (
      let index = 0;
      index < localFilesToUpload.length;
      index += BATCH_SIZE
    ) {
      const batch = localFilesToUpload.slice(index, index + BATCH_SIZE)
      await Promise.all(batch.map((file) => uploadFile(file, file.name)))
    }
  }

  return { succeeded, failed }
}

export default uploadDisputeAttachments
